import React from 'react';
import { InterestRatesValue, AppConfigParameter } from '../../types';
import { AppConfigNumberInput } from './AppConfigNumberInput';
import { UpdateConfigStatus } from '../../hooks/useAppConfig';

interface InterestRatesConfigProps {
  param: AppConfigParameter;
  localParamValue: AppConfigParameter;
  handleNestedValueChange: (parameterId: string, path: string, value: string) => void;
  updateAppConfigParameter: (id: string, updates: Partial<AppConfigParameter>) => void;
  currentParamUpdateStatus?: UpdateConfigStatus;
}

export const InterestRatesConfig: React.FC<InterestRatesConfigProps> = ({
  param,
  localParamValue,
  handleNestedValueChange,
  updateAppConfigParameter,
  currentParamUpdateStatus
}) => {
  const rates = localParamValue.value as InterestRatesValue;

  if (!rates) {
    return (
      <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
        <p className="text-sm text-orange-700 mb-3">La configuración de tasas de interés no ha sido inicializada.</p>
        <button
          onClick={() => updateAppConfigParameter('interest_rates', {
            id: 'interest_rates',
            name: 'Tasas de Interés (Simulador)',
            value: {
              visThreshold: 213525000,
              credito: { vis: 0.1130, noVis: 0.1225 },
              leasing: { vis: 0.1100, noVis: 0.1170 }
            }
          })}
          className="px-4 py-2 bg-orange-600 text-white rounded-md text-xs font-bold hover:bg-orange-700 transition-colors"
        >
          Inicializar con Valores por Defecto
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4 pt-2">
      <p className="text-xs text-gray-500 bg-sky-50 p-3 rounded-lg border border-sky-100 italic">
        Estos valores se utilizan en el simulador de compra que ven los clientes. Cambiarlos actualizará las proyecciones financieras en tiempo real.
      </p>
      
      <div className="max-w-md">
        <AppConfigNumberInput
          label="Tope Vivienda VIS (COP) - Umbral para cambio de tasa"
          value={rates.visThreshold}
          onChange={val => handleNestedValueChange(param.id, 'visThreshold', val)}
          isCurrency
          disabled={currentParamUpdateStatus?.loading}
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 border-t border-gray-100 pt-6">
        <div className="space-y-4 bg-gray-50/50 p-4 rounded-xl border border-gray-100">
          <h5 className="font-bold text-sky-900 border-b pb-2 mb-2 text-xs uppercase tracking-wider">Crédito Hipotecario</h5>
          <AppConfigNumberInput
            label="Tasa VIS (E.A.) para inmuebles <= tope"
            value={rates.credito.vis}
            onChange={val => handleNestedValueChange(param.id, 'credito.vis', val)}
            isPercentage
            disabled={currentParamUpdateStatus?.loading}
          />
          <AppConfigNumberInput
            label="Tasa No VIS (E.A.) para inmuebles > tope"
            value={rates.credito.noVis}
            onChange={val => handleNestedValueChange(param.id, 'credito.noVis', val)}
            isPercentage
            disabled={currentParamUpdateStatus?.loading}
          />
        </div>

        <div className="space-y-4 bg-gray-50/50 p-4 rounded-xl border border-gray-100">
          <h5 className="font-bold text-sky-900 border-b pb-2 mb-2 text-xs uppercase tracking-wider">Leasing Habitacional</h5>
          <AppConfigNumberInput
            label="Tasa VIS (E.A.) para inmuebles <= tope"
            value={rates.leasing.vis}
            onChange={val => handleNestedValueChange(param.id, 'leasing.vis', val)}
            isPercentage
            disabled={currentParamUpdateStatus?.loading}
          />
          <AppConfigNumberInput
            label="Tasa No VIS (E.A.) para inmuebles > tope"
            value={rates.leasing.noVis}
            onChange={val => handleNestedValueChange(param.id, 'leasing.noVis', val)}
            isPercentage
            disabled={currentParamUpdateStatus?.loading}
          />
        </div>
      </div>
    </div>
  );
};
